class PremiumVideoCardGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.cards = Array.from(this.querySelectorAll('[data-video-card]'));
    this.videos = this.cards.map((card) => card.querySelector('video')).filter(Boolean);
    this.visibleCards = new Set();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayVideos = this.dataset.autoplay === 'true' && !this.reduceMotion;
    this.hoverPreview =
      !this.autoplayVideos &&
      this.dataset.hoverPreview === 'true' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.classList.add('is-ready');
    this.setupCarousel();

    this.onClick = this.handleClick.bind(this);
    this.onPointerEnter = this.handlePointerEnter.bind(this);
    this.onPointerLeave = this.handlePointerLeave.bind(this);
    this.onVisibilityChange = this.handleVisibilityChange.bind(this);
    this.onBlockSelect = this.handleBlockSelect.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.cards.forEach((card) => {
      card.addEventListener('pointerenter', this.onPointerEnter);
      card.addEventListener('pointerleave', this.onPointerLeave);
      const video = card.querySelector('video');
      if (!video) return;

      video.defaultMuted = true;
      video.muted = true;
      video.loop = card.dataset.loop === 'true';
      this.setSoundState(card, false);
      video.addEventListener('play', this.handleVideoPlay);
      video.addEventListener('pause', this.handleVideoPause);
      video.addEventListener('ended', this.handleVideoEnded);
      video.addEventListener('volumechange', this.handleVolumeChange);
    });

    if ('IntersectionObserver' in window) {
      this.cardObserver = new IntersectionObserver(this.handleCardIntersection, {
        rootMargin: '100px 0px',
        threshold: [0, 0.25],
      });
      this.cards.forEach((card) => this.cardObserver.observe(card));

      this.revealObserver = new IntersectionObserver(this.handleRevealIntersection, {
        rootMargin: '140px 0px',
        threshold: 0.08,
      });
      this.revealObserver.observe(this);
    } else {
      this.dataset.revealed = 'true';
      this.cards.forEach((card) => {
        this.visibleCards.add(card);
        this.ensureVideoLoaded(card);
      });
      this.requestAutoplay();
    }
  }

  disconnectedCallback() {
    clearInterval(this.carouselTimer);
    this.carouselAbort?.abort();
    this.carouselObserver?.disconnect();
    this.initialized = false;
    this.pauseAll({ resetUserState: true, resetSound: true });
    this.cardObserver?.disconnect();
    this.revealObserver?.disconnect();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    this.cards?.forEach((card) => {
      card.removeEventListener('pointerenter', this.onPointerEnter);
      card.removeEventListener('pointerleave', this.onPointerLeave);
      const video = card.querySelector('video');
      if (!video) return;
      video.removeEventListener('play', this.handleVideoPlay);
      video.removeEventListener('pause', this.handleVideoPause);
      video.removeEventListener('ended', this.handleVideoEnded);
      video.removeEventListener('volumechange', this.handleVolumeChange);
    });
  }

  handleClick(event) {
    const soundToggle = event.target.closest?.('[data-sound-toggle]');
    if (soundToggle && this.contains(soundToggle)) {
      const card = soundToggle.closest('[data-video-card]');
      this.toggleSound(card);
      return;
    }

    const videoToggle = event.target.closest?.('[data-video-toggle]');
    if (!videoToggle || !this.contains(videoToggle)) return;

    const card = videoToggle.closest('[data-video-card]');
    const video = card?.querySelector('video');
    if (!card || !video) return;

    if (video.paused) {
      this.playCard(card, { userInitiated: true, withSound: !video.muted });
    } else {
      this.pauseCard(card, { resetUserState: true, resetSound: true, userPaused: true });
    }
  }

  handlePointerEnter(event) {
    if (!this.hoverPreview) return;
    const card = event.currentTarget;
    if (!card.querySelector('video') || card.dataset.userPaused === 'true') return;
    this.playCard(card, { userInitiated: false, withSound: false });
  }

  handlePointerLeave(event) {
    if (!this.hoverPreview) return;
    const card = event.currentTarget;
    if (card.dataset.userPlaying === 'true') return;
    this.pauseCard(card, { resetUserState: false, resetSound: true });
  }

  handleVisibilityChange() {
    if (!document.hidden) {
      this.requestAutoplay();
      return;
    }
    this.pauseAll({ resetUserState: false, resetSound: true });
  }

  handleBlockSelect(event) {
    const card = event.target.closest?.('[data-video-card]');
    if (!card || !this.contains(card)) return;
    this.carouselPaused = true;
    this.track?.scrollTo({ left: card.offsetLeft - this.track.firstElementChild.offsetLeft, behavior: 'auto' });
    card.classList.remove('is-editor-selected');
    requestAnimationFrame(() => card.classList.add('is-editor-selected'));
    window.setTimeout(() => card.classList.remove('is-editor-selected'), 900);
    if (this.autoplayVideos) this.playCard(card, { userInitiated: false, withSound: false });
  }

  handleCardIntersection = (entries) => {
    entries.forEach((entry) => {
      const card = entry.target;
      if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
        this.visibleCards.add(card);
        this.ensureVideoLoaded(card);
      } else {
        this.visibleCards.delete(card);
        card.dataset.userPaused = 'false';
        if (!card.querySelector('video')?.paused) {
          this.pauseCard(card, { resetUserState: true, resetSound: true });
        } else {
          this.setSoundState(card, false);
        }
      }
    });
    this.requestAutoplay();
  };

  handleRevealIntersection = (entries) => {
    if (!entries[0]?.isIntersecting) return;
    this.dataset.revealed = 'true';
    this.revealObserver?.disconnect();
  };

  handleVideoPlay = (event) => {
    const card = event.currentTarget.closest('[data-video-card]');
    this.setCardPlaybackState(card, true);
  };

  handleVideoPause = (event) => {
    const card = event.currentTarget.closest('[data-video-card]');
    this.setCardPlaybackState(card, false);
  };

  handleVideoEnded = (event) => {
    const card = event.currentTarget.closest('[data-video-card]');
    if (card?.dataset.loop === 'true') return;
    this.pauseCard(card, { resetUserState: true, resetSound: true, resetTime: true, userPaused: true });
  };

  handleVolumeChange = (event) => {
    const video = event.currentTarget;
    const card = video.closest('[data-video-card]');
    this.setSoundState(card, !video.muted && video.volume > 0);
  };

  async playCard(card, { userInitiated = false, withSound = false } = {}) {
    const video = card?.querySelector('video');
    if (!video) return;

    this.ensureVideoLoaded(card);

    if (withSound) this.muteAll({ except: card });
    video.muted = !withSound;
    video.loop = card.dataset.loop === 'true';
    card.dataset.userPlaying = String(userInitiated);
    card.dataset.userPaused = 'false';
    this.setSoundState(card, withSound);

    try {
      await video.play();
      this.setCardPlaybackState(card, true);
    } catch (_error) {
      video.muted = true;
      this.setSoundState(card, false);
      card.dataset.userPlaying = 'false';
      this.setCardPlaybackState(card, false);
    }
  }

  pauseCard(
    card,
    { resetUserState = false, resetSound = false, resetTime = false, userPaused = false } = {},
  ) {
    const video = card?.querySelector('video');
    if (!video) return;
    video.pause();
    if (resetSound) {
      video.muted = true;
      this.setSoundState(card, false);
    }
    if (resetTime) video.currentTime = 0;
    if (resetUserState) card.dataset.userPlaying = 'false';
    if (userPaused) card.dataset.userPaused = 'true';
    this.setCardPlaybackState(card, false);
  }

  pauseAll({ resetUserState = false, resetSound = false } = {}) {
    this.cards?.forEach((card) => this.pauseCard(card, { resetUserState, resetSound }));
  }

  muteAll({ except } = {}) {
    this.cards?.forEach((card) => {
      if (card === except) return;
      const video = card.querySelector('video');
      if (video) video.muted = true;
      this.setSoundState(card, false);
    });
  }

  async toggleSound(card) {
    const video = card?.querySelector('video');
    if (!video) return;

    const shouldEnable = video.muted || video.volume === 0;
    if (shouldEnable) {
      this.muteAll({ except: card });
      video.muted = false;
      video.volume = 1;
      this.setSoundState(card, true);
      if (video.paused) await this.playCard(card, { userInitiated: true, withSound: true });
    } else {
      video.muted = true;
      this.setSoundState(card, false);
    }
  }

  requestAutoplay() {
    if (!this.autoplayVideos || document.hidden) return;
    this.visibleCards.forEach((card) => {
      const video = card.querySelector('video');
      if (!video || !video.paused || card.dataset.userPaused === 'true') return;
      this.playCard(card, { userInitiated: false, withSound: false });
    });
  }

  ensureVideoLoaded(card) {
    const video = card?.querySelector('video');
    if (!video || video.dataset.sourcesLoaded === 'true') return video;

    let hasSource = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      hasSource = true;
    });

    video.dataset.sourcesLoaded = 'true';
    if (hasSource) video.load();
    return video;
  }

  setCardPlaybackState(card, playing) {
    if (!card) return;
    card.classList.toggle('is-playing', playing);
    const toggle = card.querySelector('[data-video-toggle]');
    const title = card.dataset.videoTitle || 'video';
    toggle?.setAttribute('aria-label', playing ? `Pause ${title}` : `Play ${title}`);
    toggle?.setAttribute('aria-pressed', String(playing));
  }

  setSoundState(card, soundOn) {
    if (!card) return;
    card.classList.toggle('is-sound-on', soundOn);
    const toggle = card.querySelector('[data-sound-toggle]');
    const title = card.dataset.videoTitle || 'video';
    toggle?.setAttribute('aria-label', `${soundOn ? 'Turn sound off for' : 'Turn sound on for'} ${title}`);
    toggle?.setAttribute('aria-pressed', String(soundOn));
  }

  setupCarousel() {
    this.track = this.querySelector('[data-reel-track]');
    if (!this.track) return;
    this.carouselAbort = new AbortController();
    const options = { signal: this.carouselAbort.signal };
    this.carouselPaused = false;
    this.carouselHovered = false;
    this.carouselVisible = false;
    this.carouselInteractingUntil = 0;
    const pause = this.querySelector('[data-reel-pause]');
    const updatePause = () => {
      if (!pause) return;
      pause.textContent = this.carouselPaused ? 'Resume carousel' : 'Pause carousel';
      pause.setAttribute('aria-pressed', String(this.carouselPaused));
    };
    if (pause) pause.hidden = this.reduceMotion || this.dataset.carouselAutoplay !== 'true';
    this.querySelector('[data-reel-next]')?.addEventListener('click', () => this.moveReel(1), options);
    this.querySelector('[data-reel-prev]')?.addEventListener('click', () => this.moveReel(-1), options);
    pause?.addEventListener('click', () => {
      this.carouselPaused = !this.carouselPaused;
      updatePause();
    }, options);
    this.track.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') this.carouselHovered = true; }, options);
    this.track.addEventListener('pointerleave', () => { this.carouselHovered = false; }, options);
    this.track.addEventListener('pointerdown', () => { this.carouselInteractingUntil = Date.now() + 6000; }, options);
    this.track.addEventListener('wheel', () => { this.carouselInteractingUntil = Date.now() + 6000; }, { ...options, passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.moveReel(event.key === 'ArrowRight' ? 1 : -1);
    }, options);
    this.carouselObserver = new IntersectionObserver(([entry]) => { this.carouselVisible = entry.isIntersecting; }, { threshold: 0.1 });
    this.carouselObserver.observe(this.track);
    this.carouselTimer = setInterval(() => {
      if (this.dataset.carouselAutoplay !== 'true' || this.reduceMotion || document.hidden || !this.carouselVisible || this.carouselPaused || this.carouselHovered || Date.now() < this.carouselInteractingUntil || this.track.contains(document.activeElement)) return;
      if (this.cards.some(card => card.classList.contains('is-sound-on') || card.dataset.userPlaying === 'true')) return;
      this.moveReel(1);
    }, 3000);
    updatePause();
  }

  moveReel(direction) {
    if (!this.track || this.cards.length < 2) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (max < 2) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = this.cards[0].offsetWidth + gap;
    const current = this.track.scrollLeft;
    let target = Math.min(max, Math.max(0, current + direction * step));
    if (direction > 0 && current >= max - 2) target = 0;
    if (direction < 0 && current <= 2) target = max;
    this.track.scrollTo({ left: target, behavior: this.reduceMotion || Math.abs(target - current) > step + 2 ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('premium-video-card-gallery')) {
  customElements.define('premium-video-card-gallery', PremiumVideoCardGallery);
}
